import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Copy, CheckCheck, ExternalLink, Trophy } from "lucide-react";
import { getTopReferrers } from "../adminApi";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function AdminReferrers() {
    const { includeSeed, refreshKey } = useOutletContext();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [earnedOnly, setEarnedOnly] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        getTopReferrers(50, includeSeed)
            .then((d) => alive && setItems(d.items || []))
            .catch(() => alive && setItems([]))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [includeSeed, refreshKey]);

    const filtered = earnedOnly
        ? items.filter((x) => x.free_year_earned)
        : items;

    return (
        <div className="space-y-6" data-testid="admin-referrers">
            <div>
                <h2 className="text-2xl font-semibold tracking-tight text-klareet-TEXT">
                    Top Referrers
                </h2>
                <p className="mt-1 text-sm text-klareet-MUT">
                    Respondents earning toward 1 year of Klareet free.
                </p>
            </div>

            <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-klareet-bor bg-klareet-card text-xs cursor-pointer">
                    <input
                        type="checkbox"
                        checked={earnedOnly}
                        onChange={(e) => setEarnedOnly(e.target.checked)}
                        className="accent-klareet-T2"
                        data-testid="earned-only-toggle"
                    />
                    <span className="text-klareet-MUT">Earned only</span>
                </label>
            </div>

            <div className="rounded-2xl border border-klareet-bor bg-klareet-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[11px] uppercase tracking-[0.16em] text-klareet-MUT font-mono">
                            <tr className="border-b border-klareet-bor">
                                <th className="px-4 py-3 text-left">Rank</th>
                                <th className="px-4 py-3 text-left">Code</th>
                                <th className="px-4 py-3 text-left">Name</th>
                                <th className="px-4 py-3 text-left">Email</th>
                                <th className="px-4 py-3 text-left w-[260px]">Referrals</th>
                                <th className="px-4 py-3 text-left">Status</th>
                                <th className="px-4 py-3 text-left">Earned at</th>
                                <th className="px-4 py-3 text-right">Open</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="p-10 text-center text-klareet-MUT">
                                        Loading…
                                    </td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="p-10 text-center text-klareet-MUT">
                                        No referrers yet.
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((r, i) => (
                                    <ReferrerRow key={r.share_code} r={r} rank={i + 1} />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function ReferrerRow({ r, rank }) {
    const [copied, setCopied] = useState(false);
    const goal = 20;
    const pct = Math.min(100, Math.round((r.referral_count / goal) * 100));
    function copy() {
        navigator.clipboard?.writeText(r.share_code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    }
    return (
        <tr className="border-b border-klareet-bor hover:bg-klareet-NAVY/30">
            <td className="px-4 py-3 font-mono tabular-nums text-klareet-MUT">
                {rank.toString().padStart(2, "0")}
            </td>
            <td className="px-4 py-3">
                <button
                    type="button"
                    onClick={copy}
                    className="inline-flex items-center gap-2 px-2 py-1 rounded-md border border-klareet-bor bg-klareet-NAVY font-mono text-klareet-T2 text-xs"
                    data-testid={`copy-code-${r.share_code}`}
                >
                    {r.share_code}
                    {copied ? <CheckCheck size={12} /> : <Copy size={12} />}
                </button>
            </td>
            <td className="px-4 py-3 max-w-[160px] truncate">
                {r.name || <span className="text-klareet-DIM">—</span>}
            </td>
            <td className="px-4 py-3 max-w-[220px] truncate">
                {r.email || <span className="text-klareet-DIM">—</span>}
            </td>
            <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 rounded-full bg-klareet-BOR overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-klareet-T to-klareet-T2 rounded-full transition-[width] duration-700"
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    <span className="font-mono tabular-nums text-xs text-klareet-MUT w-14 text-right">
                        {r.referral_count} / {goal}
                    </span>
                </div>
            </td>
            <td className="px-4 py-3">
                {r.free_year_earned ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-klareet-GREEN/50 text-klareet-GREEN bg-klareet-GREEN/10 text-[11px] font-medium">
                        <Trophy size={10} />
                        Earned
                    </span>
                ) : (
                    <span className="text-klareet-MUT text-xs font-mono">
                        {Math.max(0, 20 - r.referral_count)} to go
                    </span>
                )}
            </td>
            <td className="px-4 py-3 font-mono text-xs text-klareet-MUT">
                {fmtDate(r.earned_at)}
            </td>
            <td className="px-4 py-3 text-right">
                <a
                    href={`/r/${r.share_code}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-klareet-bor text-klareet-MUT hover:text-klareet-T2 hover:border-klareet-T transition-colors"
                    aria-label="Open dashboard"
                    data-testid={`open-${r.share_code}`}
                >
                    <ExternalLink size={14} />
                </a>
            </td>
        </tr>
    );
}
